import React, { useState, useRef, useEffect } from "react";
import { motion, useScroll, useTransform, useAnimation, useInView, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { IoIosArrowRoundForward, IoIosArrowRoundBack } from "react-icons/io";
import { Link } from "react-router-dom";
// Import logo instead of video
import LogoWhite from "../../assets/icons/logo-white.png";

// Import necessary icons for particles and visual elements
import { FaCode, FaMobileAlt, FaPaintBrush, FaLaptopCode, FaRegLightbulb } from "react-icons/fa";

const HeroSection = () => {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language === "ar";
  const language = i18n.language;
  const heroRef = useRef(null);
  const inView = useInView(heroRef, { once: false });
  const mainControls = useAnimation();
  
  // State for typed text animation
  const [typedText, setTypedText] = useState("");
  const [isTyping, setIsTyping] = useState(true);
  const [textIndex, setTextIndex] = useState(0);
  
  // Scroll-based animations
  const { scrollY } = useScroll();
  const y1 = useTransform(scrollY, [0, 300], [0, -100]);
  const y2 = useTransform(scrollY, [0, 300], [0, -50]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  // Heading text animation variants
  const headingVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { 
        duration: 0.8, 
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // Word-by-word animation for the main title
  const titleWords = isArabic ? 
    t("slang").split(' ').reverse() : 
    t("slang").split(' ');
  
  const titleWordVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: 0.3 + (i * 0.1),
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      }
    })
  };

  // Animation for the subtitle
  const subtitleVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: 0.5, 
        duration: 0.8,
        ease: [0.22, 1, 0.36, 1]
      } 
    }
  };

  // CTA button animation
  const buttonVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { 
        delay: 0.8, 
        duration: 0.6 
      } 
    },
    hover: { 
      scale: 1.05, 
      boxShadow: "0px 10px 25px rgba(255, 215, 0, 0.25)",
      transition: { duration: 0.3 } 
    },
    tap: { scale: 0.98 }
  };

  // Floating 3D shapes animation
  const shapeVariants = {
    initial: { opacity: 0, scale: 0 },
    animate: { opacity: 1, scale: 1, transition: { duration: 0.8 } }
  };

  // Animation for floating icons
  const floatingIconVariants = {
    hidden: { opacity: 0, scale: 0 },
    visible: (i) => ({
      opacity: 0.7,
      scale: 1,
      transition: {
        delay: 0.2 * i,
        duration: 0.8,
        ease: "easeOut"
      }
    }),
    float: (i) => ({
      y: [0, -15, 0],
      rotate: [0, i % 2 === 0 ? 10 : -10, 0],
      transition: {
        duration: 3 + (i % 3),
        repeat: Infinity,
        ease: "easeInOut"
      }
    })
  };

  // Animation for the logo
  const logoVariants = {
    hidden: { opacity: 0, scale: 0.8, rotateY: 90 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      rotateY: 0,
      transition: { 
        duration: 1.2,
        type: "spring",
        stiffness: 50
      }
    },
    pulse: {
      scale: [1, 1.05, 1],
      opacity: [1, 0.9, 1],
      transition: {
        duration: 4,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  // Animation for the grid lines
  const gridLineVariants = {
    hidden: { opacity: 0, pathLength: 0 },
    visible: (i) => ({
      opacity: 0.2,
      pathLength: 1,
      transition: {
        delay: 0.5 + (i * 0.1),
        duration: 1.5,
        ease: "easeInOut"
      }
    })
  };
  
  // Cursor blink animation
  const cursorVariants = {
    blink: {
      opacity: [0, 1, 0],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        repeatType: "loop"
      }
    }
  };

  useEffect(() => {
    if (inView) {
      mainControls.start("visible");
    }
  }, [inView, mainControls]);
  
  // Typed text animation effect
  useEffect(() => {
    // Array of phrases to type
    const phrases = [
      t("hero_subtitle")
    ];
    
    let typingTimer;
    let deletingTimer;
    
    // Ultra-fast typing effect with proper pauses
    const typeText = () => {
      const currentPhrase = phrases[textIndex];
      
      if (isTyping && typedText.length < currentPhrase.length) {
        // Type multiple characters at once (3-5 chars per update)
        const charsToAdd = Math.min(4, currentPhrase.length - typedText.length);
        setTypedText(currentPhrase.substring(0, typedText.length + charsToAdd));
        // Ultra-fast typing (5ms fixed delay)
        typingTimer = setTimeout(typeText, 5);
      } else if (isTyping && typedText.length === currentPhrase.length) {
        // Stay on complete text for 3 seconds, then set isTyping to false
        deletingTimer = setTimeout(() => {
          setIsTyping(false);
        }, 3000);
      } else if (!isTyping && typedText.length > 0) {
        // Delete text in chunks (25% of remaining text)
        const charsToDelete = Math.max(5, Math.floor(typedText.length / 4));
        setTypedText(typedText.substring(0, typedText.length - charsToDelete));
        deletingTimer = setTimeout(typeText, 5);
      } else if (!isTyping && typedText.length === 0) {
        // Wait 2 seconds before starting to type again
        deletingTimer = setTimeout(() => {
          setIsTyping(true);
        }, 2000);
      }
    };
    
    // Start immediately
    typingTimer = setTimeout(typeText, 100);
    
    return () => {
      clearTimeout(typingTimer);
      clearTimeout(deletingTimer);
    };
  }, [typedText, isTyping, textIndex, t]);

  const scrollToSection = (sectionId) => {
    const section = document.getElementById(sectionId);
    if (section) {
      const yOffset = -100;
      const y = section.getBoundingClientRect().top + window.pageYOffset + yOffset;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  // Icons for floating animation
  const floatingIcons = [
    { icon: <FaCode />, size: 24, position: { top: '15%', left: '10%' } },
    { icon: <FaMobileAlt />, size: 28, position: { top: '25%', left: '85%' } },
    { icon: <FaPaintBrush />, size: 26, position: { top: '65%', left: '15%' } },
    { icon: <FaLaptopCode />, size: 30, position: { top: '75%', left: '80%' } },
    { icon: <FaRegLightbulb />, size: 24, position: { top: '40%', left: '90%' } }
  ];

  return (
    <div
      id="hero"
      ref={heroRef}
      className="relative w-full min-h-screen overflow-hidden flex flex-col justify-center items-center bg-gradient-to-b from-black to-gray-900"
      dir={language === "ar" ? "rtl" : "ltr"}
    >
      {/* Background grid pattern */}
      <div className="absolute inset-0 w-full h-full">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGZpbGw9IiMxMjEyMTIiIGQ9Ik0wIDBoNjB2NjBIMHoiLz48cGF0aCBkPSJNMzAgMzBoMzB2MzBIMzB6TTAgMGgzMHYzMEgweiIgZmlsbD0iIzE4MTgxOCIvPjwvZz48L3N2Zz4=')] opacity-40"></div>
      </div>

      {/* Animated grid lines */}
      <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="gridGradient" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="rgba(255,215,0,0.1)" />
            <stop offset="50%" stopColor="rgba(255,215,0,0.05)" />
            <stop offset="100%" stopColor="rgba(255,215,0,0.02)" />
          </linearGradient>
        </defs>
        {/* Horizontal lines */}
        {[...Array(6)].map((_, i) => (
          <motion.line
            key={`h-${i}`}
            x1="0"
            y1={`${(i + 1) * 16}%`}
            x2="100%"
            y2={`${(i + 1) * 16}%`}
            stroke="url(#gridGradient)"
            strokeWidth="1"
            custom={i}
            variants={gridLineVariants}
            initial="hidden"
            animate="visible"
          />
        ))}
        {/* Vertical lines */}
        {[...Array(6)].map((_, i) => (
          <motion.line
            key={`v-${i}`}
            x1={`${(i + 1) * 16}%`}
            y1="0"
            x2={`${(i + 1) * 16}%`}
            y2="100%"
            stroke="url(#gridGradient)"
            strokeWidth="1"
            custom={i + 6}
            variants={gridLineVariants}
            initial="hidden"
            animate="visible"
          />
        ))}
      </svg>

      {/* Animated gradient overlay */}
      <div className="absolute inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-black/80 via-black/60 to-transparent"></div>
        <motion.div 
          className="absolute top-0 left-0 w-full h-full"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
        >
          {/* Animated gradient blobs */}
          <motion.div 
            className="absolute top-1/4 -left-48 w-96 h-96 rounded-full bg-primary/10 blur-[100px]"
            animate={{ 
              x: [0, 50, 0],
              y: [0, -30, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ 
              duration: 15,
              repeat: Infinity,
              repeatType: "reverse" 
            }}
          />
          <motion.div 
            className="absolute bottom-1/4 -right-48 w-96 h-96 rounded-full bg-primary/15 blur-[100px]"
            animate={{ 
              x: [0, -50, 0],
              y: [0, 30, 0],
              opacity: [0.3, 0.6, 0.3]
            }}
            transition={{ 
              duration: 18,
              repeat: Infinity,
              repeatType: "reverse" 
            }}
          />
        </motion.div>
      </div>

      {/* Floating icons with animations */}
      {floatingIcons.map((item, i) => (
        <motion.div
          key={`floating-icon-${i}`}
          className="absolute text-primary pointer-events-none"
          style={{ 
            top: item.position.top, 
            left: item.position.left,
            fontSize: item.size
          }}
          variants={floatingIconVariants}
          custom={i}
          initial="hidden"
          animate={["visible", "float"]}
        >
          {item.icon}
        </motion.div>
      ))}

      {/* Subtle particle effects */}
      <div className="absolute inset-0 w-full h-full overflow-hidden">
        {Array(20).fill().map((_, i) => {
          const size = 1 + Math.random() * 3;
          const xPos = Math.random() * 100;
          const yPos = Math.random() * 100;
          const duration = 20 + Math.random() * 40;
          const delay = Math.random() * 10;
          
          return (
            <motion.div 
              key={`particle-${i}`}
              className="absolute bg-primary/30 rounded-full"
              style={{ 
                width: size,
                height: size,
                left: `${xPos}%`,
                top: `${yPos}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0, 0.8, 0],
              }}
              transition={{
                duration: duration,
                repeat: Infinity,
                delay: delay,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </div>

      {/* Animated connection lines */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" xmlns="http://www.w3.org/2000/svg">
        {[...Array(8)].map((_, i) => {
          const startX = Math.random() * 100;
          const startY = Math.random() * 100;
          const endX = Math.random() * 100;
          const endY = Math.random() * 100;
          
          return (
            <motion.line
              key={`connection-${i}`}
              x1={`${startX}%`}
              y1={`${startY}%`}
              x2={`${endX}%`}
              y2={`${endY}%`}
              stroke="rgba(255, 215, 0, 0.1)"
              strokeWidth="1"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ 
                pathLength: [0, 1, 0],
                opacity: [0, 0.3, 0]
              }}
              transition={{ 
                duration: 10 + (i * 2),
                repeat: Infinity,
                delay: i * 3,
                ease: "easeInOut"
              }}
            />
          );
        })}
      </svg>

      {/* Logo with glow effect */}
      <div className="absolute top-1/3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 pointer-events-none z-30">
        <motion.div 
          className="relative"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 2 }}
        >
          {/* Glowing effect behind logo */}
          <motion.div 
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-40 h-40 md:w-56 md:h-56 rounded-full bg-primary/20 blur-[50px]"
            animate={{ 
              scale: [1, 1.2, 1],
              opacity: [0.4, 0.7, 0.4] 
            }}
            transition={{ 
              duration: 6,
              repeat: Infinity,
              repeatType: "reverse" 
            }}
          />
          
          {/* Animated rings around logo */}
          {[...Array(3)].map((_, i) => (
            <motion.div
              key={`ring-${i}`}
              className="absolute top-1/2 left-1/2 border border-primary/20 rounded-full"
              style={{
                width: `${(i + 1) * 80}px`,
                height: `${(i + 1) * 80}px`,
                marginLeft: `-${(i + 1) * 40}px`,
                marginTop: `-${(i + 1) * 40}px`,
              }}
              animate={{ 
                scale: [1, 1.2, 1],
                opacity: [0.2, 0.4, 0.2],
                rotate: [0, 360]
              }}
              transition={{ 
                duration: 20 + (i * 5),
                repeat: Infinity,
                ease: "linear"
              }}
            />
          ))}
          
          {/* Logo */}
          <motion.img 
            src={LogoWhite} 
            alt="Aruba Logo"
            className="relative w-40 h-40 md:w-56 md:h-56 object-contain opacity-100 drop-shadow-[0_0_15px_rgba(255,215,0,0.3)]"
            variants={logoVariants}
            initial="hidden"
            animate={["visible", "pulse"]}
          />
        </motion.div>
      </div>

      {/* Main content */}
      <div className="container relative z-20 mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col items-center text-center max-w-4xl mx-auto mt-[300px] md:mt-[400px]">
          {/* Main headline with word-by-word animation */}
          <motion.div
            initial="hidden"
            animate={mainControls}
            className="w-full"
          >
            <motion.h2 
              className="text-lg sm:text-xl md:text-2xl text-white font-light mb-2"
              variants={headingVariants}
            >
              {t("creativity")}
              <span className={`text-primary ${language === "en" ? "ml-2" : "mr-2"}`}>
                {t("is_our")}
              </span>
            </motion.h2>
            
            <div className="overflow-hidden mb-6">
              <motion.h1 
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white flex flex-wrap justify-center gap-x-4"
              >
                {titleWords.map((word, i) => (
                  <motion.span
                    key={i}
                    custom={i}
                    variants={titleWordVariants}
                    className="inline-block"
                  >
                    {word}
                  </motion.span>
                ))}
              </motion.h1>
            </div>
            
            {/* Typed text animation for subtitle */}
            <motion.div
              className="mt-6 text-base sm:text-lg md:text-xl text-gray-300 max-w-2xl mx-auto min-h-[60px] flex justify-center items-center"
              variants={subtitleVariants}
            >
              <span>{typedText}</span>
              <motion.span 
                className="inline-block w-[2px] h-[1em] bg-primary ml-1"
                variants={cursorVariants}
                animate="blink"
              />
            </motion.div>
          </motion.div>

          {/* CTA Buttons with hover effects */}
          <motion.div
            className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-4 sm:gap-6 justify-center items-center w-full"
            variants={buttonVariants}
            initial="hidden"
            animate={mainControls}
          >
            <motion.button
              onClick={() => scrollToSection("Services")}
              className="relative overflow-hidden group px-6 py-3 bg-primary text-black rounded-lg font-medium flex items-center justify-center gap-2 shadow-lg w-full sm:w-auto min-w-[180px]"
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
              aria-label={t("what_we_do")}
            >
              <span className="relative z-10">{t("what_we_do")}</span>
              <motion.span 
                className="relative z-10 bg-black/90 rounded-full p-1 transition-transform duration-300"
                animate={{ x: 0 }}
                whileHover={{ x: 5 }}
              >
                {language === "en" ? (
                  <IoIosArrowRoundForward className="w-5 h-5 text-primary" />
                ) : (
                  <IoIosArrowRoundBack className="w-5 h-5 text-primary" />
                )}
              </motion.span>
              
              {/* Button hover effect */}
              <motion.div 
                className="absolute inset-0 bg-primary/80" 
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </motion.button>
            
            <Link
              to="/Portfolio"
              className="group relative overflow-hidden px-6 py-3 border border-primary/30 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-all duration-300 hover:border-primary w-full sm:w-auto min-w-[180px]"
              aria-label={t("view_works")}
            >
              <span className="relative z-10">{t("view_works")}</span>
              <motion.span 
                className="relative z-10 bg-primary/10 rounded-full p-1"
                animate={{ x: 0 }}
                whileHover={{ x: 5, backgroundColor: "rgba(255, 215, 0, 0.2)" }}
                transition={{ duration: 0.3 }}
              >
                {language === "en" ? (
                  <IoIosArrowRoundForward className="w-5 h-5 text-primary" />
                ) : (
                  <IoIosArrowRoundBack className="w-5 h-5 text-primary" />
                )}
              </motion.span>
              
              {/* Button hover effect */}
              <motion.div 
                className="absolute inset-0 bg-primary/5" 
                initial={{ x: "-100%" }}
                whileHover={{ x: 0 }}
                transition={{ duration: 0.3 }}
              />
            </Link>
          </motion.div>
        </div>
      </div>

      {/* Animated scroll indicator */}
      <motion.div 
        className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 1.5, duration: 0.8 }}
      >
        <motion.div 
          className="w-6 h-10 rounded-full border-2 border-white/30 flex justify-center items-start p-1"
          initial={{ opacity: 0.5 }}
          animate={{ opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <motion.div 
            className="w-1.5 h-1.5 bg-primary rounded-full"
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          />
        </motion.div>
      </motion.div>
    </div>
  );
};

export default HeroSection;